import React, { useId } from 'react';
import styled from '@emotion/styled';

import { GrowthbookFlag } from '../../../constants/feature-flags';
import { useFeatureFlagIsOn } from '../../../hooks/feature-flags';
import { useMatchesViewport } from '../../../hooks/media';
import type { ArticleImage } from '../../../types/Article';
import ImageCarousel from '../../image-carousel/ImageCarousel';
import type { ImageCarouselNavigationProps } from '../../image-carousel/ImageCarouselNavigation';
import { NextControlButton, PrevControlButton } from '../../image-carousel/ImageCarouselNavigation';
import { HIGH_PRIORITY_IMAGE_COUNT, PRODUCT_CARD_IMAGE_SIZE } from '../constants/constants';
import { trackImageCarouselNavigation } from '../tracking/tracking';
import ResponsiveProductImage from './ResponsiveProductImage';

const NavigationOnHoverWrapper = styled.div`
  position: relative;

  .navigation-button {
    opacity: 0;
    transition: opacity 200ms ease;

    &:disabled {
      opacity: 0;
    }
  }

  &:hover .navigation-button {
    opacity: 0.8;

    &:disabled {
      opacity: 0.5;
    }
  }
`;

interface ProductCardImageProps {
  images: ArticleImage[];
  index: number;
  isUpdated: boolean;
  visibleProductsCount: number;
  name: string;
}

const ProductCardImage = ({
  images,
  index,
  isUpdated,
  visibleProductsCount,
  name,
}: ProductCardImageProps) => {
  const baseId = useId();
  const withSwipePLPGallery = useFeatureFlagIsOn(GrowthbookFlag.SwipePLPGallery);
  const {
    matches: { matchesDesktop },
  } = useMatchesViewport();

  if (withSwipePLPGallery) {
    const isBelowTheFold = index >= visibleProductsCount;

    const renderControls = (props: ImageCarouselNavigationProps) => (
      <>
        <PrevControlButton
          hasPrev={props.hasPrev}
          onPrevious={props.onPrevious}
          arrowIconSize={props.arrowIconSize}
          className="navigation-button"
        />
        <NextControlButton
          hasNext={props.hasNext}
          onNext={props.onNext}
          arrowIconSize={props.arrowIconSize}
          className="navigation-button"
        />
      </>
    );

    const carousel = (
      <ImageCarousel
        key={images[0]?.path}
        items={images.map(image => ({ path: image.path }))}
        name={name}
        withContainerAriaLabel={false}
        paginationStyles={{
          marginBottom: 'sp_8',
        }}
        widths={PRODUCT_CARD_IMAGE_SIZE}
        baseId={baseId}
        showNavigation={matchesDesktop}
        renderControls={renderControls}
        onNext={(imageIndex: number) => {
          trackImageCarouselNavigation({
            direction: 'next',
            list_index: index,
            image_index: imageIndex,
          });
        }}
        onPrevious={(imageIndex: number) => {
          trackImageCarouselNavigation({
            direction: 'previous',
            list_index: index,
            image_index: imageIndex,
          });
        }}
        isLazy={isBelowTheFold && !isUpdated}
        highPriority={!isBelowTheFold && index < HIGH_PRIORITY_IMAGE_COUNT}
        progressiveLoading
      />
    );

    return <NavigationOnHoverWrapper>{carousel}</NavigationOnHoverWrapper>;
  }

  return (
    <ResponsiveProductImage
      images={images}
      widths={PRODUCT_CARD_IMAGE_SIZE}
      fallbackSize={PRODUCT_CARD_IMAGE_SIZE}
      alt={name}
      imgIndex={index}
      isReplaced={isUpdated}
      key={images[0]?.path}
      visibleProductsCount={visibleProductsCount}
    />
  );
};

export default ProductCardImage;
